/**
 * OldNewDiff — "what is on the policy today" beside "what ora would make it".
 *
 * Story 26739. Visual contract: the `.diff-grid` block in
 * `tasks/design/step-views.html` — a three-column grid (field · was · now)
 * with the old value struck through and the new value in weight.
 *
 * The brief's job is to be approvable in one read, and the single thing that
 * makes a change approvable is seeing what it REPLACES. A list of new values
 * alone asks the reader to hold the old ones in their head.
 *
 * WHERE THE OLD VALUE COMES FROM (design decision D2, PROPOSED). There is no
 * `before`/`after` pair on a HITL payload today: the substrate sends the draft
 * it wants approved and says nothing about what it displaces. So callers
 * assemble the pairs from what the gate does carry — the mortgagee draft plus
 * the selected existing interest's label — and a row with no old value renders
 * the design's em dash WITH an explicit screen-reader phrase, because a bare
 * dash on its own can be read as "unchanged", which is the one thing it must
 * never mean. When the substrate grows a real before/after this component does
 * not change; its callers stop guessing.
 *
 * AND THE MIRROR OF THAT RULE: a strike-through must never be drawn over a
 * value that is not being replaced. Editing one field of a multi-field record
 * (a mortgagee's address, the name untouched) leaves the other fields with
 * `before === after`, and striking those through told the reader the name was
 * replaced by itself — undermining the one read the brief is supposed to be
 * approvable in. `changed` carries that verdict, from the source that built the
 * rows where possible and derived here when the caller did not set it.
 *
 * CONFIDENCE. The chip is the SHARED `ConfidenceChip` — the one the launch
 * form already renders — and it appears only on fields `confidenceChip.ts`
 * says were INFERRED from the email. Deviation from the artboards, recorded
 * deliberately: they show a percentage ("96%") and the substrate publishes a
 * LEVEL (`high | medium | low`). Rendering an invented percentage would put a
 * number on the screen that no upstream system computed.
 *
 * No Office.js, no Remix server import (design Constraint 20).
 */
import type { ReactNode } from "react";
import {
  ConfidenceChip,
  type ConfidenceLevel,
} from "../components/confidenceChip";

export interface OldNewRow {
  /** What the value is, in the reader's words ("Mortgagee", "Loan #"). */
  label: string;
  /**
   * What is on the policy today. `null`/`""` means "we found none", which
   * renders as the design's em dash plus explicit assistive copy.
   */
  before?: string | null;
  /** What it would become. */
  after: string;
  /**
   * Whether this field actually CHANGES. `false` means `before` and `after` are
   * the same value — the row still renders (it is context the reader needs to
   * approve the rest) but the old value must not be struck through, because a
   * strike-through says "this is being replaced" about something that is not.
   *
   * Absent means "work it out from the values", so a caller that predates the
   * flag still cannot render a false `was X → now X`. Set it at the SOURCE
   * anyway (see `stepViewModel.diffRows`): every consumer of those rows, not
   * only this renderer, should be able to tell an edit from an unchanged field.
   */
  changed?: boolean;
  /** Only for a field ora INFERRED from the email — see `confidenceChip.ts`. */
  confidence?: ConfidenceLevel;
}

export interface OldNewDiffProps {
  rows: readonly OldNewRow[];
  /** Section heading above the grid. Omit for none. */
  heading?: ReactNode;
  /** The "Change type … · Replace mortgagee" line above the grid. */
  lead?: ReactNode;
  /** Rendered instead of the grid when `rows` is empty. */
  emptyCopy?: string;
}

/** Shown in place of an old value we do not have. */
export const NO_EXISTING_VALUE_MARK = "—";
/** What a screen reader hears instead of that dash. */
export const NO_EXISTING_VALUE_COPY = "not on the policy today";

export function OldNewDiff({
  rows,
  heading,
  lead,
  emptyCopy = "Nothing on the policy changes.",
}: OldNewDiffProps) {
  return (
    <div className="flex flex-col gap-1.5" data-testid="old-new-diff">
      {heading ? (
        <p className="text-2xs font-semibold uppercase tracking-wide text-primary-gray-light-500">
          {heading}
        </p>
      ) : null}
      {lead ? (
        <p
          className="text-[11.5px] text-primary-gray-light-700"
          data-testid="old-new-diff-lead"
        >
          {lead}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p
          className="text-xs text-primary-gray-light-600"
          data-testid="old-new-diff-empty"
        >
          {emptyCopy}
        </p>
      ) : (
        <div
          className="grid grid-cols-[70px_1fr_1fr] items-start gap-x-2 gap-y-[7px] text-[11px]"
          role="table"
          aria-label="Proposed change"
        >
          <span />
          <span className="text-[9.5px] uppercase tracking-[0.06em] text-primary-gray-light-500">
            was
          </span>
          <span className="text-[9.5px] uppercase tracking-[0.06em] text-primary-gray-light-500">
            now
          </span>

          {rows.map((row, i) => {
            const before =
              typeof row.before === "string" && row.before.trim() !== ""
                ? row.before.trim()
                : null;
            // No flag from the caller → derive it, so an unflagged row can
            // never strike through a value it is replacing with itself.
            const changed =
              row.changed ?? (before === null || before !== row.after.trim());
            return (
              <RowCells
                key={`${row.label}-${i}`}
                label={row.label}
                before={before}
                after={row.after}
                changed={changed}
                confidence={row.confidence}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * One row, as three grid children.
 *
 * A wrapper element around them would break the parent grid's column
 * alignment, which is the whole point of the layout — so this returns a
 * fragment rather than a `<div>`, and the `data-testid`s live on the cells.
 */
function RowCells({
  label,
  before,
  after,
  changed,
  confidence,
}: {
  label: string;
  before: string | null;
  after: string;
  /** False → same value on both sides: shown, never struck through. */
  changed: boolean;
  confidence?: ConfidenceLevel;
}) {
  return (
    <>
      <span
        className="pt-px text-[10.5px] text-primary-gray-light-500"
        data-testid="old-new-field"
      >
        {label}
      </span>
      <span
        className={
          before && changed
            ? "text-primary-gray-light-500 line-through"
            : "text-primary-gray-light-500"
        }
        data-testid="old-new-before"
        data-changed={changed ? "true" : "false"}
      >
        {before ?? (
          <>
            <span aria-hidden="true">{NO_EXISTING_VALUE_MARK}</span>
            <span className="sr-only">{NO_EXISTING_VALUE_COPY}</span>
          </>
        )}
      </span>
      <span
        className="font-semibold text-primary-gray-light-900"
        data-testid="old-new-after"
      >
        {after}
        {confidence ? (
          <span className="ml-1.5 align-middle">
            <ConfidenceChip confidence={confidence} />
          </span>
        ) : null}
      </span>
    </>
  );
}

export default OldNewDiff;
